import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import { getSupabase } from "@/lib/supabase";
import Breadcrumb from "@/components/base/Breadcrumb";
import PageMeta from "@/components/base/PageMeta";

const siteUrl = import.meta.env.VITE_PUBLIC_SITE_URL || "https://hackerslodge.com";

const cryptoPaymentSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  url: `${siteUrl}/payment/crypto`,
  name: "Pay with Cryptocurrency — Hackers Lodge",
  description: "Pay for hacking and cybersecurity services with Bitcoin, Ethereum, USDT, and other cryptocurrencies on Hackers Lodge. Secure escrow and crypto payment options available.",
  isPartOf: {
    "@type": "WebSite",
    name: "Hackers Lodge",
    url: siteUrl,
  },
};

interface CryptoWallet {
  id: number;
  coin_name: string;
  coin_symbol: string;
  wallet_address: string;
  network: string;
  qr_code_url: string | null;
}

const coinIcons: Record<string, string> = {
  BTC: "fab fa-bitcoin",
  ETH: "fab fa-ethereum",
  USDT: "fas fa-dollar-sign",
  USDC: "fas fa-dollar-sign",
  LTC: "fas fa-litecoin-sign",
  XMR: "fas fa-coins",
  SOL: "fas fa-circle",
  BNB: "fas fa-circle",
  MATIC: "fas fa-circle",
};

const coinColors: Record<string, { bg: string; text: string; border: string }> = {
  BTC: { bg: "bg-amber-900/20", text: "text-amber-500", border: "border-amber-500" },
  ETH: { bg: "bg-indigo-50", text: "text-indigo-500", border: "border-indigo-500" },
  USDT: { bg: "bg-emerald-50", text: "text-emerald-500", border: "border-emerald-500" },
  USDC: { bg: "bg-sky-50", text: "text-sky-500", border: "border-sky-500" },
  LTC: { bg: "bg-[#161b22]", text: "text-gray-500", border: "border-gray-500" },
  XMR: { bg: "bg-orange-50", text: "text-orange-500", border: "border-orange-500" },
  SOL: { bg: "bg-purple-50", text: "text-purple-500", border: "border-purple-500" },
  BNB: { bg: "bg-yellow-50", text: "text-yellow-500", border: "border-yellow-500" },
  MATIC: { bg: "bg-violet-50", text: "text-violet-500", border: "border-violet-500" },
};

export default function CryptoPaymentPage() {
  const navigate = useNavigate();
  const [wallets, setWallets] = useState<CryptoWallet[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCoin, setSelectedCoin] = useState<string | null>(null);
  const [usdtNetwork, setUsdtNetwork] = useState<string>("TRC20");
  const [copiedAddress, setCopiedAddress] = useState<string | null>(null);

  useEffect(() => {
    fetchWallets();
  }, []);

  const fetchWallets = async () => {
    setLoading(true);
    try {
      const supabase = getSupabase();
      const { data, error } = await supabase
        .from("crypto_wallets")
        .select("*")
        .order("id", { ascending: true });
      if (error) throw error;
      setWallets(data || []);
    } catch {
      setWallets([]);
    } finally {
      setLoading(false);
    }
  };

  // Group wallets by coin_symbol
  const grouped = wallets.reduce<Record<string, CryptoWallet[]>>((acc, w) => {
    if (!acc[w.coin_symbol]) acc[w.coin_symbol] = [];
    acc[w.coin_symbol].push(w);
    return acc;
  }, {});

  const uniqueCoins = Object.entries(grouped).map(([symbol, entries]) => ({
    symbol,
    coinName: entries[0].coin_name,
    entries,
  }));

  // Get active USDT wallet based on selected network
  const getSelectedWallet = (): CryptoWallet | undefined => {
    if (!selectedCoin) return undefined;
    const entries = grouped[selectedCoin];
    if (!entries || entries.length === 0) return undefined;

    if (selectedCoin === "USDT") {
      const match = entries.find((w) => w.network === usdtNetwork);
      return match || entries[0];
    }
    return entries[0];
  };

  const activeWallet = getSelectedWallet();
  const hasNetworks = selectedCoin === "USDT" && (grouped[selectedCoin]?.length || 0) > 1;
  const availableNetworks = selectedCoin === "USDT" ? (grouped[selectedCoin] || []).map((e) => e.network) : [];

  const copyToClipboard = async (address: string) => {
    try {
      await navigator.clipboard.writeText(address);
      setCopiedAddress(address);
      setTimeout(() => setCopiedAddress(null), 2000);
    } catch {
      // fallback
    }
  };

  const handleContinue = () => {
    const category = sessionStorage.getItem("selectedServiceCategory") || "Other";
    // Save the selected coin/network so the post-project form can reference it
    sessionStorage.setItem("cryptoPaymentCoin", selectedCoin || "");
    if (selectedCoin === "USDT") {
      sessionStorage.setItem("cryptoPaymentNetwork", usdtNetwork);
    }
    navigate(`/post-project/${encodeURIComponent(category)}`);
  };

  return (
    <div className="min-h-screen bg-[#161b22]">
      <PageMeta
        title="Pay with Cryptocurrency — Bitcoin, Ethereum, USDT | Hackers Lodge"
        description="Pay for cybersecurity services with Bitcoin, Ethereum, USDT, and other cryptocurrencies on Hackers Lodge. Secure escrow and crypto payment options available for all hacking and penetration testing projects."
        keywords="pay cryptocurrency, Bitcoin payment, Ethereum payment, USDT payment, crypto escrow, hacker payment, cybersecurity payment"
        canonicalUrl={`${siteUrl}/payment/crypto`}
        ogType="website"
        schema={cryptoPaymentSchema}
      />
      <Header />
      <main className="pt-20 md:pt-24">
        {/* Hero */}
        <section className="relative py-12 md:py-16 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460]" />
          <div className="absolute inset-0 opacity-[0.03] bg-[radial-gradient(circle_at_50%_50%,_white_1px,_transparent_1px)] bg-[length:24px_24px]" />
          <div className="relative w-full px-4 md:px-8 max-w-5xl mx-auto text-center">
            <div className="w-14 h-14 md:w-16 md:h-16 bg-[#0d1117]/10 rounded-2xl flex items-center justify-center mx-auto mb-4 backdrop-blur-sm">
              <i className="fab fa-bitcoin text-[#5cb85c] text-xl md:text-2xl" />
            </div>
            <h1 className="text-2xl md:text-4xl font-bold text-white mb-3 tracking-tight">
              Pay with Cryptocurrency
            </h1>
            <p className="text-sm md:text-base text-gray-300 max-w-xl mx-auto leading-relaxed">
              Select your preferred cryptocurrency below. For USDT payments, choose your network first.
            </p>
          </div>
        </section>

        {/* Coin Selection */}
        <section className="py-8 md:py-12">
          <div className="w-full px-4 md:px-8 max-w-2xl mx-auto">
            <Breadcrumb items={[{ label: "Home", to: "/" }, { label: "Post Project", to: "/post-project" }, { label: "Crypto Payment" }]} />

            {loading ? (
              <div className="flex items-center justify-center py-16">
                <i className="fas fa-spinner fa-spin text-[#5cb85c] text-xl" />
              </div>
            ) : wallets.length === 0 ? (
              <div className="text-center py-16 bg-[#0d1117] rounded-xl border border-gray-700">
                <div className="w-16 h-16 bg-[#1c2128] rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="fas fa-wallet text-gray-400 text-xl" />
                </div>
                <p className="text-sm text-gray-500">No crypto wallets configured yet.</p>
              </div>
            ) : (
              <>
                {/* Step 1: Choose Coin */}
                <div className="mb-8">
                  <h2 className="text-lg font-bold text-gray-200 mb-4 flex items-center gap-2">
                    <span className="w-7 h-7 rounded-full bg-[#5cb85c] text-white text-xs font-bold flex items-center justify-center">1</span>
                    Choose Your Cryptocurrency
                  </h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {uniqueCoins.map((coin) => {
                      const color = coinColors[coin.symbol] || { bg: "bg-[#161b22]", text: "text-gray-500", border: "border-gray-400" };
                      const isSelected = selectedCoin === coin.symbol;
                      return (
                        <button
                          key={coin.symbol}
                          onClick={() => setSelectedCoin(coin.symbol)}
                          className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all cursor-pointer ${
                            isSelected
                              ? `${color.border} ${color.bg} shadow-md scale-[1.02]`
                              : "border-gray-700 bg-[#0d1117] hover:border-gray-600 hover:shadow-sm"
                          }`}
                        >
                          <div className={`w-10 h-10 rounded-full ${color.bg} flex items-center justify-center ${isSelected ? "scale-110" : ""} transition-transform`}>
                            <i className={`${coinIcons[coin.symbol] || "fas fa-coins"} ${color.text} text-base`} />
                          </div>
                          <span className="text-xs font-bold text-gray-300">{coin.coinName}</span>
                          <span className="text-[10px] font-semibold text-gray-400 bg-[#1c2128] rounded-md px-2 py-0.5">{coin.symbol}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Step 2: Network selection (USDT only) */}
                {selectedCoin && hasNetworks && (
                  <div className="mb-8">
                    <h2 className="text-lg font-bold text-gray-200 mb-4 flex items-center gap-2">
                      <span className="w-7 h-7 rounded-full bg-[#5cb85c] text-white text-xs font-bold flex items-center justify-center">2</span>
                      Select Network
                    </h2>
                    <div className="flex flex-wrap gap-2">
                      {availableNetworks.map((net) => (
                        <button
                          key={net}
                          onClick={() => setUsdtNetwork(net)}
                          className={`px-4 py-2.5 rounded-lg border text-sm font-semibold transition-all cursor-pointer ${
                            usdtNetwork === net
                              ? "border-emerald-500 bg-emerald-50 text-emerald-700 shadow-sm"
                              : "border-gray-700 bg-[#0d1117] text-gray-400 hover:border-gray-600"
                          }`}
                        >
                          {net}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Step 3: Wallet Address + Post Project */}
                {selectedCoin && activeWallet && (
                  <div className="mb-6">
                    <h2 className="text-lg font-bold text-gray-200 mb-4 flex items-center gap-2">
                      <span className="w-7 h-7 rounded-full bg-[#5cb85c] text-white text-xs font-bold flex items-center justify-center">{hasNetworks ? "3" : "2"}</span>
                      Send to This Address
                    </h2>
                    <div className="bg-[#0d1117] border border-gray-700 rounded-xl p-5">
                      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-4">
                        <div className="flex items-center gap-2">
                          <div className={`w-8 h-8 rounded-full ${(coinColors[selectedCoin] || coinColors.BTC).bg} flex items-center justify-center`}>
                            <i className={`${coinIcons[selectedCoin] || "fas fa-coins"} ${(coinColors[selectedCoin] || coinColors.BTC).text} text-xs`} />
                          </div>
                          <div>
                            <span className="text-sm font-bold text-gray-200">
                              {activeWallet.coin_name}
                            </span>
                            {hasNetworks && (
                              <span className="text-xs text-emerald-600 font-semibold ml-1">({usdtNetwork})</span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* QR Code + Address side by side */}
                      <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 mb-3">
                        {activeWallet.qr_code_url && (
                          <div className="w-36 h-36 rounded-xl border-2 border-gray-600 bg-white p-2 flex-shrink-0">
                            <img
                              src={activeWallet.qr_code_url}
                              alt={`${activeWallet.coin_name} wallet QR code`}
                              className="w-full h-full object-contain"
                            />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-gray-500 mb-1.5 font-semibold">Wallet Address</p>
                          <div className="bg-[#161b22] border border-gray-700 rounded-lg p-3">
                            <code className="text-sm text-gray-300 break-all font-mono">{activeWallet.wallet_address}</code>
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                        <button
                          onClick={() => copyToClipboard(activeWallet.wallet_address)}
                          className={`flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all whitespace-nowrap ${
                            copiedAddress === activeWallet.wallet_address
                              ? "bg-emerald-500 text-white"
                              : "bg-[#5cb85c] hover:bg-[#4cae4c] text-white"
                          }`}
                        >
                          <i className={`fas ${copiedAddress === activeWallet.wallet_address ? "fa-check" : "fa-copy"} text-xs`} />
                          {copiedAddress === activeWallet.wallet_address ? "Copied!" : "Copy Address"}
                        </button>
                        <button
                          onClick={handleContinue}
                          disabled={!selectedCoin}
                          className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all whitespace-nowrap bg-white hover:bg-gray-100 text-[#0d1117] disabled:bg-gray-600 disabled:text-gray-400 disabled:cursor-not-allowed"
                        >
                          <i className="fas fa-arrow-right text-xs" />
                          Continue
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Warning */}
                <div className="p-4 bg-amber-900/20 border border-amber-700/30 rounded-xl">
                  <p className="text-xs text-amber-400 leading-relaxed">
                    <i className="fas fa-exclamation-triangle mr-2" />
                    <strong>Important:</strong> Double-check the wallet address before sending funds.
                    Cryptocurrency transactions are irreversible. Always verify the network matches
                    the address displayed above.
                  </p>
                </div>
              </>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}